import { t } from "@lingui/macro";

import type { MarketInfo, MarketsInfoData } from "domain/synthetics/markets/types";
import { getMarketIndexName, getMarketPoolName } from "domain/synthetics/markets/utils";
import { OrderType } from "domain/synthetics/orders";
import type { TokenData } from "domain/synthetics/tokens";
import { adaptToV1TokenInfo, getTokensRatioByAmounts } from "domain/synthetics/tokens/utils";
import { getExchangeRateDisplay } from "lib/legacy";
import { formatBalanceAmount } from "lib/numbers";
import type { Token, TokenInfo } from "sdk/types/tokens";
import { SwapTradeAction, TradeActionType } from "sdk/types/tradeHistory";

import {
  INEQUALITY_GT,
  INEQUALITY_LT,
  MakeOptional,
  RowDetails,
  formatTradeActionTimestamp,
  formatTradeActionTimestampISO,
  getErrorTooltipTitle,
  infoRow,
  lines,
  tryGetError,
} from "./shared";
import { getActionTitle } from "../../keys";

const ELLIPSIS = "...";
const ARROW_SEPARATOR = " → ";
const NOT_AVAILABLE = t`N/A`;

const hasPositiveAmount = (value: bigint | undefined): value is bigint => value !== undefined && value > 0n;

/**
 * getTokensRatioByAmounts return the same type as the input. But types are TokenData.
 */
const adapt = (token: Token | undefined): TokenInfo | undefined => {
  return token ? adaptToV1TokenInfo(token as TokenData) : undefined;
};

export const formatSwapMessage = (
  tradeAction: SwapTradeAction,
  marketsInfoData?: MarketsInfoData,
  relativeTimestamp = true
): RowDetails => {
  const tokenIn = tradeAction.initialCollateralToken;

  const tokenOut = tradeAction.targetCollateralToken!;
  const hasSwapPath = Array.isArray(tradeAction.swapPath) && tradeAction.swapPath.length > 0;
  const hasInputAmount = hasPositiveAmount(tradeAction.initialCollateralDeltaAmount);
  const hasExecutionAmount = hasPositiveAmount(tradeAction.executionAmountOut);
  const hasMinOutputAmount = hasPositiveAmount(tradeAction.minOutputAmount);
  let amountIn = tradeAction.initialCollateralDeltaAmount ?? 0n;

  if (
    tradeAction.twapParams &&
    (tradeAction.eventName === TradeActionType.OrderCreated || tradeAction.eventName === TradeActionType.OrderCancelled)
  ) {
    amountIn = amountIn * BigInt(tradeAction.twapParams.numberOfParts);
  }

  const fromText = hasInputAmount
    ? formatBalanceAmount(amountIn, tokenIn.decimals, tokenIn.symbol, {
        isStable: tokenIn.isStable,
        showZero: true,
      })
    : NOT_AVAILABLE;
  const fromAmountText = hasInputAmount
    ? formatBalanceAmount(amountIn, tokenIn.decimals, undefined, {
        isStable: tokenIn.isStable,
        showZero: true,
      })
    : NOT_AVAILABLE;

  const toMinText = hasMinOutputAmount
    ? formatBalanceAmount(tradeAction.minOutputAmount, tokenOut?.decimals, tokenOut?.symbol, {
        isStable: tokenOut?.isStable,
        showZero: true,
      })
    : NOT_AVAILABLE;

  const tokensExecutionRatio =
    hasExecutionAmount && hasInputAmount
      ? getTokensRatioByAmounts({
          fromToken: tokenIn,
          toToken: tokenOut,
          fromTokenAmount: amountIn,
          toTokenAmount: tradeAction.executionAmountOut!,
        })
      : undefined;

  const tokensMinRatio =
    hasMinOutputAmount && hasInputAmount
      ? getTokensRatioByAmounts({
          fromToken: tokenIn,
          toToken: tokenOut,
          fromTokenAmount: amountIn,
          toTokenAmount: tradeAction.minOutputAmount!,
        })
      : undefined;

  const acceptablePriceInequality =
    tokensMinRatio?.largestToken?.address === tokenOut?.address ? INEQUALITY_LT : INEQUALITY_GT;

  const executionRate = tokensExecutionRatio
    ? getExchangeRateDisplay(
        tokensExecutionRatio?.ratio,
        adapt(tokensExecutionRatio?.smallestToken),
        adapt(tokensExecutionRatio?.largestToken)
      )
    : NOT_AVAILABLE;

  const acceptableRate = tokensMinRatio
    ? getExchangeRateDisplay(
        tokensMinRatio?.ratio,
        adapt(tokensMinRatio?.smallestToken),
        adapt(tokensMinRatio?.largestToken)
      )
    : NOT_AVAILABLE;

  const pathTokenSymbols = hasSwapPath
    ? getSwapPathTokenSymbols(marketsInfoData, tokenIn, tradeAction.swapPath!)
    : undefined;

  const market = !pathTokenSymbols?.length ? NOT_AVAILABLE : pathTokenSymbols.join(ARROW_SEPARATOR);

  const fullMarket = !marketsInfoData
    ? NOT_AVAILABLE
    : tradeAction.swapPath
        ?.filter((marketAddress) => marketsInfoData?.[marketAddress])
        .map((marketAddress) => marketsInfoData?.[marketAddress]?.name ?? ELLIPSIS)
        .join(ARROW_SEPARATOR) || NOT_AVAILABLE;

  const fullMarketNames: RowDetails["fullMarketNames"] = hasSwapPath
    ? getSwapPathMarketFullNames(marketsInfoData, tradeAction.swapPath)
    : undefined;

  let actionText = getActionTitle(tradeAction.orderType, tradeAction.eventName, Boolean(tradeAction.twapParams));

  let result: MakeOptional<RowDetails, "action" | "market" | "timestamp" | "timestampISO">;

  const ot = tradeAction.orderType;
  const ev = tradeAction.eventName;

  if (tradeAction.twapParams) {
    if (ev === TradeActionType.OrderExecuted) {
      const toExecutionText = formatBalanceAmount(
        tradeAction.executionAmountOut ?? 0n,
        tokenOut?.decimals,
        tokenOut?.symbol,
        { isStable: tokenOut?.isStable, showZero: true }
      );
      const toExecutionAmountText = hasExecutionAmount
        ? formatBalanceAmount(tradeAction.executionAmountOut!, tokenOut?.decimals, undefined, {
            isStable: tokenOut?.isStable,
            showZero: true,
          })
        : NOT_AVAILABLE;
      result = {
        price: executionRate,
        priceComment: lines(t`Execution price for the order.`, "", infoRow(t`Order Acceptable Price`, t`N/A`)),
        size: hasExecutionAmount ? t`${fromText} to ${toExecutionText}` : t`${fromText} to ${NOT_AVAILABLE}`,
        swapToTokenAmount: toExecutionAmountText,
      };
    } else {
      const error = tradeAction.reasonBytes ? tryGetError(tradeAction.reasonBytes) ?? undefined : undefined;
      const errorComment = error
        ? lines({
            text: getErrorTooltipTitle(error.name, false),
            state: "error",
          })
        : undefined;

      const errorActionComment =
        ev === TradeActionType.OrderFrozen || ev === TradeActionType.OrderCancelled ? errorComment : undefined;

      result = {
        price: t`N/A`,
        priceComment: null,
        size: t`${fromText} to ${NOT_AVAILABLE}`,
        swapToTokenAmount: NOT_AVAILABLE,
        actionComment: errorActionComment,
        isActionError: Boolean(errorActionComment),
      };
    }
  } else if (
    (ot === OrderType.LimitSwap && ev === TradeActionType.OrderCreated) ||
    (ot === OrderType.LimitSwap && ev === TradeActionType.OrderUpdated) ||
    (ot === OrderType.LimitSwap && ev === TradeActionType.OrderCancelled)
  ) {
    const toMinAmountText = hasMinOutputAmount
      ? formatBalanceAmount(tradeAction.minOutputAmount, tokenOut?.decimals, undefined, {
          isStable: tokenOut?.isStable,
        })
      : NOT_AVAILABLE;
    result = {
      price: tokensMinRatio ? `${acceptablePriceInequality}${acceptableRate}` : NOT_AVAILABLE,
      priceComment: lines(
        t`Acceptable price for the order.`,
        "",
        t`The trigger price for this order is based on the swap fees and price impact to guarantee that you will receive at least ${toMinText} on order execution.`
      ),
      size: t`${fromText} to ${toMinText}`,
      swapToTokenAmount: toMinAmountText,
    };
  } else if (ot === OrderType.LimitSwap && ev === TradeActionType.OrderExecuted) {
    const toExecutionText = formatBalanceAmount(tradeAction.executionAmountOut!, tokenOut?.decimals, tokenOut?.symbol, {
      isStable: tokenOut?.isStable,
    });
    const toExecutionAmountText = hasExecutionAmount
      ? formatBalanceAmount(tradeAction.executionAmountOut!, tokenOut?.decimals, undefined, {
          isStable: tokenOut?.isStable,
        })
      : NOT_AVAILABLE;
    result = {
      price: executionRate,
      priceComment: lines(
        t`Execution price for the order.`,
        "",
        infoRow(
          t`Order Acceptable Price`,
          tokensMinRatio ? `${acceptablePriceInequality}${acceptableRate}` : NOT_AVAILABLE
        )
      ),
      size: hasExecutionAmount ? t`${fromText} to ${toExecutionText}` : t`${fromText} to ${NOT_AVAILABLE}`,
      swapToTokenAmount: toExecutionAmountText,
    };
  } else if (ot === OrderType.LimitSwap && ev === TradeActionType.OrderFrozen) {
    const error = tradeAction.reasonBytes ? tryGetError(tradeAction.reasonBytes) ?? undefined : undefined;
    const outputAmount = error?.args?.outputAmount as bigint | undefined;
    const ratio =
      outputAmount !== undefined
        ? getTokensRatioByAmounts({
            fromToken: tokenIn,
            toToken: tokenOut,
            fromTokenAmount: amountIn,
            toTokenAmount: outputAmount,
          })
        : undefined;
    const rate = getExchangeRateDisplay(ratio?.ratio, adapt(ratio?.smallestToken), adapt(ratio?.largestToken));
    const toExecutionText = formatBalanceAmount(outputAmount ?? 0n, tokenOut?.decimals, tokenOut?.symbol, {
      isStable: tokenOut?.isStable,
    });
    const toExecutionAmountText = formatBalanceAmount(outputAmount ?? 0n, tokenOut?.decimals, undefined, {
      isStable: tokenOut?.isStable,
    });

    result = {
      actionComment:
        error &&
        lines({
          text: getErrorTooltipTitle(error.name, false),
          state: "error",
        }),
      price: rate,
      priceComment: lines(
        t`Execution price for the order.`,
        "",
        infoRow(
          t`Order Acceptable Price`,
          tokensMinRatio ? `${acceptablePriceInequality}${acceptableRate}` : NOT_AVAILABLE
        )
      ),
      size: t`${fromText} to ${toExecutionText}`,
      swapToTokenAmount: toExecutionAmountText,
      isActionError: true,
    };
  } else if (
    (ot === OrderType.MarketSwap && ev === TradeActionType.OrderCreated) ||
    (ot === OrderType.MarketSwap && ev === TradeActionType.OrderUpdated)
  ) {
    const toMinAmountText = hasMinOutputAmount
      ? formatBalanceAmount(tradeAction.minOutputAmount, tokenOut?.decimals, undefined, {
          isStable: tokenOut?.isStable,
        })
      : NOT_AVAILABLE;
    result = {
      price: tokensMinRatio ? `${acceptablePriceInequality}${acceptableRate}` : NOT_AVAILABLE,
      priceComment: lines(t`Acceptable price for the order.`),
      size: t`${fromText} to ${toMinText}`,
      swapToTokenAmount: toMinAmountText,
    };
  } else if (ot === OrderType.MarketSwap && ev === TradeActionType.OrderExecuted) {
    const toExecutionText = hasExecutionAmount
      ? formatBalanceAmount(tradeAction.executionAmountOut!, tokenOut?.decimals, tokenOut?.symbol, {
          isStable: tokenOut?.isStable,
        })
      : NOT_AVAILABLE;
    const toExecutionAmountText = hasExecutionAmount
      ? formatBalanceAmount(tradeAction.executionAmountOut!, tokenOut?.decimals, undefined, {
          isStable: tokenOut?.isStable,
        })
      : NOT_AVAILABLE;

    result = {
      price: executionRate,
      priceComment: lines(
        t`Execution price for the order.`,
        "",
        infoRow(
          t`Order Acceptable Price`,
          tokensMinRatio ? `${acceptablePriceInequality}${acceptableRate}` : NOT_AVAILABLE
        )
      ),
      size: t`${fromText} to ${toExecutionText}`,
      swapToTokenAmount: toExecutionAmountText,
    };
  } else if (ot === OrderType.MarketSwap && ev === TradeActionType.OrderCancelled) {
    const error = tradeAction.reasonBytes ? tryGetError(tradeAction.reasonBytes) ?? undefined : undefined;
    const outputAmount = error?.args?.outputAmount as bigint | undefined;
    const ratio =
      outputAmount !== undefined
        ? getTokensRatioByAmounts({
            fromToken: tokenIn,
            toToken: tokenOut,
            fromTokenAmount: amountIn,
            toTokenAmount: outputAmount,
          })
        : undefined;
    const rate = ratio
      ? getExchangeRateDisplay(ratio?.ratio, adapt(ratio?.smallestToken), adapt(ratio?.largestToken))
      : NOT_AVAILABLE;
    const toExecutionText = formatBalanceAmount(outputAmount ?? 0n, tokenOut?.decimals, tokenOut?.symbol, {
      isStable: tokenOut?.isStable,
    });
    const toExecutionAmountText =
      outputAmount !== undefined && outputAmount > 0n
        ? formatBalanceAmount(outputAmount, tokenOut?.decimals, undefined, {
            isStable: tokenOut?.isStable,
          })
        : NOT_AVAILABLE;

    result = {
      actionComment:
        error &&
        lines({
          text: getErrorTooltipTitle(error.name, true),
          state: "error",
        }),
      price: rate,
      priceComment: lines(
        t`Execution price for the order.`,
        "",
        infoRow(
          t`Order Acceptable Price`,
          tokensMinRatio ? `${acceptablePriceInequality}${acceptableRate}` : NOT_AVAILABLE
        )
      ),
      size:
        outputAmount !== undefined && outputAmount > 0n
          ? t`${fromText} to ${toExecutionText}`
          : t`${fromText} to ${NOT_AVAILABLE}`,
      swapToTokenAmount: toExecutionAmountText,
      isActionError: true,
    };
  }

  return {
    type: t`Swap`,
    action: actionText,
    market: market,
    fullMarket: fullMarket,
    timestamp: formatTradeActionTimestamp(tradeAction.timestamp, relativeTimestamp),
    timestampISO: formatTradeActionTimestampISO(tradeAction.timestamp),
    acceptablePrice: tokensMinRatio ? `${acceptablePriceInequality}${acceptableRate}` : NOT_AVAILABLE,
    executionPrice: executionRate,
    fullMarketNames,
    swapFromTokenSymbol: tokenIn.symbol,
    swapToTokenSymbol: tokenOut.symbol,
    swapFromTokenAmount: fromAmountText,
    ...result!,
  };
};

export function getSwapPathMarketFullNames(
  marketsInfoData: MarketsInfoData | undefined,
  swapPath: string[]
): { indexName: string; poolName: string }[] | undefined {
  if (!marketsInfoData) {
    return undefined;
  }

  return swapPath.map((marketAddress) => {
    const marketInfo = marketsInfoData[marketAddress];

    if (!marketInfo) {
      return {
        indexName: ELLIPSIS,
        poolName: ELLIPSIS,
      };
    }

    const indexName = getMarketIndexName({
      indexToken: marketInfo.indexToken,
      isSpotOnly: marketInfo.isSpotOnly,
    });
    const poolName = getMarketPoolName({
      longToken: marketInfo.longToken,
      shortToken: marketInfo.shortToken,
    });

    return {
      indexName: indexName,
      poolName: poolName,
    };
  });
}

export function getSwapPathTokenSymbols(
  marketsInfoData: MarketsInfoData | undefined,
  initialCollateralToken: TokenData,
  swapPath: string[]
): string[] | undefined {
  if (!marketsInfoData || !swapPath) {
    return undefined;
  }

  let pathTokenSymbolsLoading = false;

  const pathTokenSymbols: string[] = swapPath
    .map((marketAddress) => marketsInfoData[marketAddress])
    .reduce(
      (acc: TokenData[], marketInfo: MarketInfo | undefined) => {
        if (!marketInfo || pathTokenSymbolsLoading) {
          pathTokenSymbolsLoading = true;
          return [];
        }

        const last = acc[acc.length - 1];

        if (last.address === marketInfo.longToken.address) {
          acc.push(marketInfo.shortToken);
        } else if (last.address === marketInfo.shortToken.address) {
          acc.push(marketInfo.longToken);
        }

        return acc;
      },
      [initialCollateralToken] as TokenData[]
    )
    .map((token: TokenData) => token?.symbol);

  if (pathTokenSymbolsLoading) {
    return undefined;
  }

  return pathTokenSymbols;
}
